/*
 * Throwaway preview — proposed "Adjust invoice" + Credit Memo flow.
 *
 * Scenario walkthrough:
 *   Buyer ordered 12 units → invoice INV-1042 issued ($480) → buyer paid.
 *   Seller starts packing, finds only 10 sellable units in stock.
 *   Seller hits "Adjust quantities" on the invoice → drops line qty 12→10.
 *   System auto-issues Credit Memo CRM-0007 for $80 → buyer wallet credited.
 *
 * Hit /preview/invoice-adjustment to view.
 */
import { useState, useMemo } from "react";
import {
  FileText, Receipt, CheckCircle2, AlertTriangle, ArrowRight, ArrowDown,
  Wallet, Bell, ChevronRight, Pencil, X, Plus, Minus, Info, Zap,
  ClipboardCheck, ScrollText, ShieldCheck, Mail,
} from "lucide-react";

const ROLES = [
  { key: "seller", label: "Seller view" },
  { key: "buyer",  label: "Buyer view"  },
];

const REASONS = [
  { id: "short_pick",          label: "Short pick / stockout" },
  { id: "damaged_in_warehouse", label: "Damaged in warehouse" },
  { id: "quality_reject",      label: "Quality reject" },
  { id: "oversold",            label: "Oversold (channel mismatch)" },
  { id: "other",               label: "Other" },
];

// Mock invoice — paid invoice scenario (seller realises shortage post-payment)
const ORIGINAL = {
  code: "INV-1042",
  status: "paid",
  buyer:  "Apex Beauty Co.",
  seller: "GlowCo Cosmetics",
  order_code: "ORD-3119",
  paid_at: "Feb 8, 2026",
  lines: [
    { sku: "GLO-LIP-VLT-04", name: "Velvet Lipstick · Plum",   ordered: 12, price: 18.00 },
    { sku: "GLO-MSC-MID-02", name: "Midnight Mascara",         ordered: 6,  price: 22.00 },
    { sku: "GLO-BLM-DSK-01", name: "Dewy Skin Balm 30ml",      ordered: 8,  price: 14.50 },
  ],
};

export default function InvoiceAdjustmentPreview() {
  const [role, setRole] = useState("seller");
  const [step, setStep] = useState(1); // 1=invoice list, 2=modal, 3=settled
  // adjustments keyed by sku — adjusted qty and reason
  const [adj, setAdj] = useState({
    "GLO-LIP-VLT-04": { qty: 10, reason: "short_pick", note: "" },
    "GLO-MSC-MID-02": { qty: 6,  reason: "",           note: "" },
    "GLO-BLM-DSK-01": { qty: 8,  reason: "",           note: "" },
  });

  const originalTotal = useMemo(
    () => ORIGINAL.lines.reduce((s, l) => s + l.ordered * l.price, 0),
    []
  );
  const adjustedTotal = useMemo(
    () => ORIGINAL.lines.reduce((s, l) => s + (adj[l.sku]?.qty ?? l.ordered) * l.price, 0),
    [adj]
  );
  const credit = +(originalTotal - adjustedTotal).toFixed(2);
  const hasChanges = credit !== 0;

  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Sticky preview header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-[1240px] mx-auto px-8 py-4 flex items-center gap-6 flex-wrap">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/50">Preview · internal mockup · v1</div>
            <div className="display text-lg mt-0.5">Adjust invoice · auto credit memo on quantity changes</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">View:</span>
            <div className="flex items-center gap-1 rounded-full border border-border bg-white p-1">
              {ROLES.map(r => (
                <button key={r.key}
                  onClick={() => { setRole(r.key); setStep(r.key === "seller" ? 1 : 3); }}
                  data-testid={`adj-preview-tab-${r.key}`}
                  className={`rounded-full px-4 h-8 text-[12px] mono uppercase tracking-[0.12em] transition-colors ${role === r.key ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {/* Stage strip */}
        <div className="max-w-[1240px] mx-auto px-8 pb-3">
          <div className="flex items-center gap-3 text-[11px] mono uppercase tracking-[0.14em] text-foreground/55 flex-wrap">
            <span className={role === "seller" ? "text-foreground" : ""}>1 · Seller opens invoice</span>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className={role === "seller" && step >= 2 ? "text-foreground" : ""}>2 · Adjust modal</span>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className={step >= 3 ? "text-foreground" : ""}>3 · Credit memo + wallet credit</span>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className={role === "buyer" ? "text-foreground" : ""}>4 · Buyer notification</span>
          </div>
        </div>
      </div>

      <div className="max-w-[1240px] mx-auto px-8 py-10">
        {role === "seller" ? (
          <SellerView
            step={step} setStep={setStep}
            adj={adj} setAdj={setAdj}
            originalTotal={originalTotal} adjustedTotal={adjustedTotal} credit={credit}
            hasChanges={hasChanges}
          />
        ) : (
          <BuyerView credit={credit} />
        )}

        {/* Why this design wins */}
        <div className="mt-12 grid md:grid-cols-3 gap-4">
          <Pillar
            icon={<ClipboardCheck className="w-4 h-4" />}
            title="One canonical reason for money to move"
            body="Pre-shipment qty discrepancy → credit memo. Post-delivery problem → claim. No overlap, no fraud vector, no duplicate paths."
          />
          <Pillar
            icon={<ScrollText className="w-4 h-4" />}
            title="Audit-clean, append-only"
            body="Original invoice stays immutable. Credit memo CRM-#### is its own document, bidirectionally linked. Both sides see the full timeline."
          />
          <Pillar
            icon={<ShieldCheck className="w-4 h-4" />}
            title="Step-up SMS only when money moves"
            body="Adjusting an unpaid invoice → just regenerates. Adjusting a paid invoice → seller wallet → buyer wallet → SMS challenge required."
          />
        </div>

        {/* Footnote */}
        <div className="mt-8 rounded-2xl border border-dashed border-border bg-white p-5 text-[12px] text-foreground/65 leading-relaxed">
          <strong className="text-foreground">Edge cases handled:</strong>
          {" "}Increase (re-count finds more) → supplementary invoice for delta. Adjustment after status=transit blocked → use claim flow. Bulk adjust on multi-line POs uses same modal. Buyer is notified in-portal + email; refunds-team gets a copy when delta &gt; $500.
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
function SellerView({ step, setStep, adj, setAdj, originalTotal, adjustedTotal, credit, hasChanges }) {
  return (
    <div className="grid md:grid-cols-[1fr_360px] gap-8">
      <div>
        {/* Step 1 — invoice row with new "Adjust" button */}
        <SectionHeader n={1} title="On Invoices, the new “Adjust” button appears for paid/sent invoices not yet shipped" />
        <div className="rounded-2xl border border-border bg-white overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-secondary/50 text-[10px] mono uppercase tracking-[0.18em] text-foreground/60">
              <tr>
                <th className="text-left p-3 font-normal">Invoice</th>
                <th className="text-left p-3 font-normal">Buyer</th>
                <th className="text-right p-3 font-normal">Amount</th>
                <th className="text-left p-3 font-normal">Status</th>
                <th className="text-right p-3 font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border">
                <td className="p-3 mono">{ORIGINAL.code}</td>
                <td className="p-3">{ORIGINAL.buyer}</td>
                <td className="p-3 text-right mono">${originalTotal.toFixed(2)}</td>
                <td className="p-3"><Chip tone="emerald">Paid</Chip></td>
                <td className="p-3 text-right">
                  <button
                    onClick={() => setStep(2)}
                    data-testid="adj-preview-open-modal"
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 h-8 text-[12px] hover:bg-secondary/40 transition-colors">
                    <Pencil className="w-3.5 h-3.5" /> Adjust quantities
                  </button>
                </td>
              </tr>
              <tr className="border-t border-border text-foreground/55">
                <td className="p-3 mono">INV-1041</td>
                <td className="p-3">Apex Beauty Co.</td>
                <td className="p-3 text-right mono">$214.00</td>
                <td className="p-3"><Chip tone="slate">Sent</Chip></td>
                <td className="p-3 text-right text-[11px] mono uppercase tracking-[0.12em] text-foreground/40">Adjust enabled</td>
              </tr>
              <tr className="border-t border-border text-foreground/45">
                <td className="p-3 mono">INV-1039</td>
                <td className="p-3">Wholesale Hut</td>
                <td className="p-3 text-right mono">$1,840.00</td>
                <td className="p-3"><Chip tone="violet">Shipped</Chip></td>
                <td className="p-3 text-right text-[11px] mono uppercase tracking-[0.12em] text-foreground/40">Locked · use claim</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Step 2 — modal (inline mock) */}
        {step >= 2 && (
          <div className="mt-10">
            <SectionHeader n={2} title="Adjust modal · live preview of the credit calculation" />
            <div className="rounded-2xl border border-border bg-white shadow-sm">
              <div className="flex items-start justify-between p-5 border-b border-border">
                <div>
                  <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Adjust invoice</div>
                  <div className="display text-xl mt-1">{ORIGINAL.code} · {ORIGINAL.buyer}</div>
                  <div className="text-[12px] text-foreground/55 mt-1">
                    Linked order <span className="mono">{ORIGINAL.order_code}</span> · paid {ORIGINAL.paid_at}
                  </div>
                </div>
                <button onClick={() => setStep(1)} className="text-foreground/50 hover:text-foreground" data-testid="adj-preview-close-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5">
                <div className="rounded-lg bg-secondary/40 border border-border px-3 py-2 mb-4 flex items-start gap-2 text-[12px] text-foreground/70 leading-relaxed">
                  <Info className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    Use this when the packed quantity differs from the invoice. Money moves <strong>only</strong> when the invoice is already paid — otherwise we just regenerate it. Once shipped, use the claim flow instead.
                  </span>
                </div>

                {ORIGINAL.lines.map(line => {
                  const a = adj[line.sku];
                  const qty = a.qty;
                  const lineDelta = (line.ordered - qty) * line.price;
                  const changed = qty !== line.ordered;
                  return (
                    <div key={line.sku} className={`grid grid-cols-[1fr_120px_140px_120px] gap-3 py-3 border-t border-border first:border-t-0 items-start ${changed ? "bg-amber-50/40 -mx-5 px-5 rounded-md" : ""}`}>
                      <div>
                        <div className="text-[13px]">{line.name}</div>
                        <div className="text-[11px] mono text-foreground/50 mt-0.5">{line.sku} · ${line.price.toFixed(2)}/u</div>
                      </div>
                      {/* Ordered qty */}
                      <div>
                        <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50">Ordered</div>
                        <div className="mono text-[14px] mt-1">{line.ordered}</div>
                      </div>
                      {/* New qty + reason */}
                      <div>
                        <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50">New qty</div>
                        <div className="flex items-center gap-1 mt-1">
                          <button
                            onClick={() => setAdj({ ...adj, [line.sku]: { ...a, qty: Math.max(0, qty - 1) }})}
                            data-testid={`adj-preview-dec-${line.sku}`}
                            className="w-7 h-7 rounded-md border border-border bg-white hover:bg-secondary/40 flex items-center justify-center">
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <input
                            type="number"
                            value={qty}
                            onChange={e => setAdj({ ...adj, [line.sku]: { ...a, qty: Math.max(0, +e.target.value || 0) }})}
                            className="w-14 h-7 text-center mono text-[13px] rounded-md border border-border bg-white"
                            data-testid={`adj-preview-input-${line.sku}`}
                          />
                          <button
                            onClick={() => setAdj({ ...adj, [line.sku]: { ...a, qty: qty + 1 }})}
                            data-testid={`adj-preview-inc-${line.sku}`}
                            className="w-7 h-7 rounded-md border border-border bg-white hover:bg-secondary/40 flex items-center justify-center">
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {changed && (
                          <select
                            value={a.reason}
                            onChange={e => setAdj({ ...adj, [line.sku]: { ...a, reason: e.target.value }})}
                            className="mt-2 w-full h-8 text-[12px] rounded-md border border-border bg-white px-2"
                            data-testid={`adj-preview-reason-${line.sku}`}>
                            <option value="">Pick a reason…</option>
                            {REASONS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                          </select>
                        )}
                      </div>
                      {/* Delta */}
                      <div className="text-right">
                        <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50">Credit</div>
                        <div className={`mono text-[14px] mt-1 ${lineDelta > 0 ? "text-emerald-600" : lineDelta < 0 ? "text-amber-700" : "text-foreground/40"}`}>
                          {lineDelta > 0 ? `+$${lineDelta.toFixed(2)}` : lineDelta < 0 ? `-$${Math.abs(lineDelta).toFixed(2)}` : "—"}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Totals strip */}
                <div className="mt-5 rounded-xl border border-border bg-secondary/30 p-4 grid grid-cols-3 gap-4 items-center">
                  <div>
                    <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50">Original</div>
                    <div className="mono text-[15px] mt-0.5">${originalTotal.toFixed(2)}</div>
                  </div>
                  <div className="text-center">
                    <ArrowRight className="w-4 h-4 mx-auto text-foreground/40" />
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50">Adjusted</div>
                    <div className="mono text-[15px] mt-0.5">${adjustedTotal.toFixed(2)}</div>
                  </div>
                  {hasChanges && (
                    <div className="col-span-3 mt-3 pt-3 border-t border-border flex items-center justify-between">
                      <div className="text-[12px] text-foreground/65 flex items-center gap-2">
                        <Wallet className="w-4 h-4" />
                        Credit to be issued to {ORIGINAL.buyer}'s wallet
                      </div>
                      <div className="mono text-[16px] text-emerald-600">${credit.toFixed(2)}</div>
                    </div>
                  )}
                </div>

                <div className="mt-5 flex items-center justify-between">
                  <button onClick={() => setStep(1)} className="text-[12px] mono uppercase tracking-[0.14em] text-foreground/55 hover:text-foreground">
                    Cancel
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!hasChanges}
                    data-testid="adj-preview-confirm"
                    className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-5 h-10 text-[12px] mono uppercase tracking-[0.14em] hover:bg-foreground/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    {hasChanges ? <Zap className="w-3.5 h-3.5" /> : null}
                    {hasChanges ? `Confirm · issue $${credit.toFixed(2)} credit` : "No changes"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3 — settled banner */}
        {step >= 3 && (
          <div className="mt-10">
            <SectionHeader n={3} title="After confirm · seller's invoice gets a credit-memo banner" />
            <div className="rounded-2xl border border-border bg-white p-5">
              <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="text-[13px] font-medium text-emerald-900">
                    Adjusted · Credit memo CRM-0007 issued for ${credit.toFixed(2)}
                  </div>
                  <div className="text-[12px] text-emerald-800/80 mt-0.5">
                    Wallet debited from GlowCo · credited to Apex Beauty Co. · {ORIGINAL.buyer} notified by email + in-portal.
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-[11px] mono uppercase tracking-[0.12em]">
                    <a className="text-emerald-700 hover:underline" href="#">View CRM-0007</a>
                    <span className="text-emerald-700/40">·</span>
                    <a className="text-emerald-700 hover:underline" href="#">Updated invoice PDF</a>
                    <span className="text-emerald-700/40">·</span>
                    <a className="text-emerald-700 hover:underline" href="#">Updated pack list PDF</a>
                  </div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
                <Stat label="Invoice total" value={`$${originalTotal.toFixed(2)}`} dimmed />
                <Stat label="Adjusted total" value={`$${adjustedTotal.toFixed(2)}`} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right rail — annotations */}
      <aside className="space-y-3">
        <Annotation
          dot="emerald"
          title="Why on the invoice (not the order)?"
          body="Money is bound to invoices. Putting Adjust here keeps the audit trail tight and makes it obvious to the seller that the action moves money."
        />
        <Annotation
          dot="amber"
          title="Reason chips, not free text"
          body="Forced reason categorisation feeds analytics — sellers who hit short_pick weekly need restock alerts, oversold means a Shopify mismatch."
        />
        <Annotation
          dot="violet"
          title="Step-up SMS"
          body="Required when invoice is already paid (real money leaves seller wallet). Skipped on unpaid invoices — those just regenerate."
        />
        <Annotation
          dot="rose"
          title="Locked once shipped"
          body="Once status=transit/delivered, Adjust is hidden. The buyer needs to file a claim instead — bilateral evidence required."
        />
      </aside>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
function BuyerView({ credit }) {
  const originalTotal = ORIGINAL.lines.reduce((s, l) => s + l.ordered * l.price, 0);
  return (
    <div className="grid md:grid-cols-[1fr_360px] gap-8">
      <div>
        <SectionHeader n={1} title="Bell notification fires the moment seller confirms" />
        <div className="rounded-2xl border border-border bg-white p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
              <Bell className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="text-[13px] font-medium">Credit issued · ${credit.toFixed(2)}</div>
                <span className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/40">just now</span>
              </div>
              <div className="text-[12px] text-foreground/65 mt-1 leading-relaxed">
                GlowCo Cosmetics adjusted invoice <span className="mono">INV-1042</span> after a partial-pick on order <span className="mono">ORD-3119</span>.
                ${credit.toFixed(2)} credited to your wallet · Reason: <em>Short pick / stockout</em>.
              </div>
              <div className="mt-2 flex items-center gap-2">
                <a href="#" className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/65 hover:text-foreground inline-flex items-center gap-1">
                  View credit memo <ArrowRight className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <SectionHeader n={2} className="mt-10" title="Email · auto-sent via Resend" />
        <div className="rounded-2xl border border-border bg-white overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-3 border-b border-border bg-secondary/40 text-[12px]">
            <Mail className="w-4 h-4 text-foreground/55" />
            <div>
              <div className="font-medium">Credit issued · GlowCo adjusted INV-1042 (-${credit.toFixed(2)})</div>
              <div className="text-[11px] text-foreground/55 mono">From: GlowCo via TRAQO &lt;hello@traqo.com&gt;</div>
            </div>
          </div>
          <div className="p-5 text-[13px] leading-relaxed text-foreground/80 space-y-3">
            <p>Hi Apex Beauty Co.,</p>
            <p>GlowCo Cosmetics adjusted invoice <span className="mono">INV-1042</span> after a short-pick during packing. We've credited <strong>${credit.toFixed(2)}</strong> back to your TRAQO wallet — available immediately.</p>
            <div className="rounded-lg bg-secondary/40 border border-border p-4 text-[12px] mono">
              <div className="flex justify-between"><span className="text-foreground/55">Original invoice</span><span>${originalTotal.toFixed(2)}</span></div>
              <div className="flex justify-between mt-1"><span className="text-foreground/55">Credit memo CRM-0007</span><span className="text-emerald-600">−${credit.toFixed(2)}</span></div>
              <div className="flex justify-between mt-1 pt-2 border-t border-border"><span>Adjusted total</span><span>${(originalTotal - credit).toFixed(2)}</span></div>
            </div>
            <p>The pack list and updated invoice PDFs reflect the new quantities. The remaining items shipped today.</p>
            <a href="#" className="inline-flex items-center gap-1 rounded-full bg-foreground text-background px-4 h-9 text-[12px] mono uppercase tracking-[0.14em]">
              View credit memo <ArrowRight className="w-3 h-3" />
            </a>
          </div>
        </div>

        <SectionHeader n={3} className="mt-10" title="Wallet ledger · new row" />
        <div className="rounded-2xl border border-border bg-white overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-secondary/40 text-[10px] mono uppercase tracking-[0.18em] text-foreground/60">
              <tr>
                <th className="text-left p-3 font-normal">When</th>
                <th className="text-left p-3 font-normal">Type</th>
                <th className="text-left p-3 font-normal">Reference</th>
                <th className="text-right p-3 font-normal">Amount</th>
                <th className="text-right p-3 font-normal">Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border bg-emerald-50/40">
                <td className="p-3 mono text-[11px]">just now</td>
                <td className="p-3"><Chip tone="emerald">Credit memo received</Chip></td>
                <td className="p-3 mono text-[11px]">CRM-0007 · INV-1042</td>
                <td className="p-3 text-right mono text-emerald-600">+${credit.toFixed(2)}</td>
                <td className="p-3 text-right mono">${(2100 + credit).toFixed(2)}</td>
              </tr>
              <tr className="border-t border-border text-foreground/65">
                <td className="p-3 mono text-[11px]">Feb 8</td>
                <td className="p-3"><Chip tone="slate">Invoice paid</Chip></td>
                <td className="p-3 mono text-[11px]">INV-1042</td>
                <td className="p-3 text-right mono">−${originalTotal.toFixed(2)}</td>
                <td className="p-3 text-right mono">$2,100.00</td>
              </tr>
              <tr className="border-t border-border text-foreground/55">
                <td className="p-3 mono text-[11px]">Feb 5</td>
                <td className="p-3"><Chip tone="slate">Top-up</Chip></td>
                <td className="p-3 mono text-[11px]">TXN-9911</td>
                <td className="p-3 text-right mono">+$2,000.00</td>
                <td className="p-3 text-right mono">${(2100 + originalTotal).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <SectionHeader n={4} className="mt-10" title="Order page · invoice now reflects new total" />
        <div className="rounded-2xl border border-border bg-white p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">Order ORD-3119</div>
              <div className="display text-lg mt-0.5">GlowCo Cosmetics · 3 lines</div>
            </div>
            <Chip tone="violet">In transit</Chip>
          </div>
          <div className="mt-4 rounded-lg bg-secondary/30 border border-border px-4 py-3 flex items-center justify-between text-[12px]">
            <div className="flex items-center gap-2 text-foreground/65">
              <Receipt className="w-4 h-4" />
              <span>INV-1042</span>
              <ArrowDown className="w-3 h-3" />
              <span className="text-emerald-700 font-medium">Adjusted via CRM-0007</span>
            </div>
            <div className="mono text-[14px]">${originalTotal.toFixed(2)} → ${(originalTotal - credit).toFixed(2)}</div>
          </div>
        </div>
      </div>

      {/* Right rail */}
      <aside className="space-y-3">
        <Annotation
          dot="emerald"
          title="Money is real & instant"
          body="Wallet credit is available immediately for the buyer to use on the next invoice — no waiting for ACH or bank reversal."
        />
        <Annotation
          dot="amber"
          title="Why not edit the invoice in place?"
          body="Audit / accounting cleanliness. The original invoice stays as-is; the credit memo is its own immutable doc. Both linked, both downloadable."
        />
        <Annotation
          dot="violet"
          title="The buyer didn't have to do anything"
          body="No approve, no acknowledge — same as how returns work in retail. Buyer can dispute via the existing claims flow if they disagree."
        />
        <Annotation
          dot="rose"
          title="Statement integration"
          body="Monthly statements PDF will include credit memos as their own section so accounting can reconcile cleanly."
        />
      </aside>
    </div>
  );
}

/* ─── primitives ─────────────────────────────────────────────────────── */
function SectionHeader({ n, title, className = "" }) {
  return (
    <div className={`flex items-center gap-3 mb-4 ${className}`}>
      <div className="w-7 h-7 rounded-full bg-foreground text-background flex items-center justify-center text-[11px] mono">
        {n}
      </div>
      <div className="text-[13px] text-foreground/75">{title}</div>
    </div>
  );
}

function Chip({ tone = "slate", children }) {
  const tones = {
    slate:   "bg-slate-100 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    violet:  "bg-violet-50 text-violet-700 border-violet-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 h-6 text-[11px] mono uppercase tracking-[0.12em] ${tones[tone]}`}>
      {children}
    </span>
  );
}

function Annotation({ dot, title, body }) {
  const tones = {
    emerald: "bg-emerald-500",
    amber:   "bg-amber-500",
    violet:  "bg-violet-500",
    rose:    "bg-rose-500",
  };
  return (
    <div className="rounded-2xl border border-border bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-2 h-2 rounded-full ${tones[dot]}`} />
        <div className="text-[12px] mono uppercase tracking-[0.14em] text-foreground/70">{title}</div>
      </div>
      <div className="text-[12px] text-foreground/70 leading-relaxed">{body}</div>
    </div>
  );
}

function Stat({ label, value, dimmed }) {
  return (
    <div className={`rounded-lg border border-border bg-secondary/30 p-3 ${dimmed ? "opacity-60" : ""}`}>
      <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/55">{label}</div>
      <div className="mono text-[14px] mt-0.5">{value}</div>
    </div>
  );
}

function Pillar({ icon, title, body }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className="w-9 h-9 rounded-full bg-secondary/60 border border-border flex items-center justify-center text-foreground/70">
        {icon}
      </div>
      <div className="mt-3 text-[13px] font-medium">{title}</div>
      <div className="mt-1 text-[12px] text-foreground/65 leading-relaxed">{body}</div>
    </div>
  );
}
